import { styled } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { TransitionEvent } from "react";
import { useRef, useState } from "react";

const AccordionContainer = styled("div")(({ theme }) => ({
  borderRadius: "8px",
  border: "1px solid #eaeaea",
  padding: "16px",
  backgroundColor: theme.palette.background.paper,
  width: "100%",
}));

const AccordionSummary = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  margin: "-8px -16px -8px -16px",
  padding: "8px 16px 8px 16px",
  cursor: "pointer",
  alignItems: "center",
});

const AccordionTitle = styled("p")({
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "21px",
  textAlign: "left",
  margin: 0,
});

const AccordionSubtitle = styled("p")(({ theme }) => ({
  fontSize: "16px",
  fontWeight: 400,
  lineHeight: "21px",
  textAlign: "left",
  margin: 0,
  paddingTop: "8px",
  color: theme.palette.text.primary,
}));

interface CloseableProps {
  open: boolean;
}

interface AccordionDetailsProps extends CloseableProps {
  maxHeight: number;
}

const AccordionDetails = styled("div", {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "maxHeight",
})<AccordionDetailsProps>(({ open, maxHeight }) => ({
  maxHeight: open ? `${maxHeight}px` : 0,
  transition: "max-height 0.3s ease-in-out",
  overflow: "hidden",
}));

const StyledExpandIcon = styled(ExpandMoreIcon, {
  shouldForwardProp: (prop) => prop !== "open",
})<CloseableProps>(({ open, theme }) => ({
  transform: open ? "rotate(-180deg)" : undefined,
  transition: "0.3s",
  color: theme.palette.icon.primary,
}));

interface AccordionProps {
  children: React.ReactNode;
  title: string | React.ReactNode;
  subtitle?: string | React.ReactNode;
  initialMaxHeight?: number;
  expanded?: boolean;
}

export function Accordion({
  children,
  title,
  subtitle,
  initialMaxHeight = 500,
  expanded,
}: AccordionProps) {
  const [open, setOpen] = useState(expanded || false);
  const [maxHeight, setMaxHeight] = useState<number>(initialMaxHeight);
  const detailsRef = useRef<HTMLDivElement>(null);

  const handleMaxHeight = (e: TransitionEvent<HTMLDivElement>) => {
    const height = e.currentTarget.getBoundingClientRect().height;

    if (height === 0) return;
    setMaxHeight(height);
  };

  return (
    <AccordionContainer>
      <AccordionSummary onClick={() => setOpen(!open)}>
        <div>
          <AccordionTitle>{title}</AccordionTitle>
          {subtitle && <AccordionSubtitle>{subtitle}</AccordionSubtitle>}
        </div>
        <StyledExpandIcon open={open} />
      </AccordionSummary>
      <AccordionDetails
        onTransitionEnd={handleMaxHeight}
        open={open}
        maxHeight={maxHeight}
        ref={detailsRef}
      >
        {children}
      </AccordionDetails>
    </AccordionContainer>
  );
}
